import type { Option } from '@/constants/institute';
import type { TicketCategory, TicketPriority, TicketStatus } from '@/types/support';

/** One source for the words both panels use about a ticket (CLAUDE.md §3). */

export const TICKET_CATEGORY_LABEL: Record<TicketCategory, string> = {
  TECHNICAL: 'Something is not working',
  BILLING: 'Billing and subscription',
  FEATURE_REQUEST: 'Feature request',
  ACCOUNT: 'Logins and accounts',
  DATA_CORRECTION: 'Correcting data',
  OTHER: 'Something else',
};

export const TICKET_PRIORITY_LABEL: Record<TicketPriority, string> = {
  LOW: 'Low',
  NORMAL: 'Normal',
  HIGH: 'High',
  URGENT: 'Urgent',
};

/**
 * Said from the school's point of view on their screen and ours alike —
 * "Waiting on you" would be wrong on our side, so the neutral wording is used.
 */
export const TICKET_STATUS_LABEL: Record<TicketStatus, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In progress',
  WAITING_ON_SCHOOL: 'Waiting on school',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
};

/** Badge tones. Amber for "somebody needs to act", grey once it is over. */
export const TICKET_STATUS_TONE = {
  OPEN: 'info',
  IN_PROGRESS: 'info',
  WAITING_ON_SCHOOL: 'warning',
  RESOLVED: 'success',
  CLOSED: 'neutral',
} as const satisfies Record<TicketStatus, string>;

export const TICKET_PRIORITY_TONE = {
  LOW: 'neutral',
  NORMAL: 'neutral',
  HIGH: 'warning',
  URGENT: 'critical',
} as const satisfies Record<TicketPriority, string>;

function options<T extends string>(labels: Record<T, string>): Option<T>[] {
  return (Object.entries(labels) as [T, string][]).map(([value, label]) => ({ value, label }));
}

export const TICKET_CATEGORY_OPTIONS = options(TICKET_CATEGORY_LABEL);
export const TICKET_PRIORITY_OPTIONS = options(TICKET_PRIORITY_LABEL);
export const TICKET_STATUS_OPTIONS = options(TICKET_STATUS_LABEL);

/** Matches the server: five files a message, each a PDF or an image. */
export const TICKET_ATTACHMENT_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'] as const;
export const TICKET_ATTACHMENT_LABEL = 'PDF, PNG, JPG or WEBP';
export const MAX_TICKET_ATTACHMENTS = 5;
export const MAX_TICKET_ATTACHMENT_MB = 5;
